/**
 * Metadati di pagina — title, description, canonical, Open Graph, Twitter,
 * JSON-LD.
 *
 * Perche' serve un modulo e non tre righe in ogni pagina
 * ----------------------------------------------------
 * In un'applicazione a pagina singola c'e' **un solo `index.html`**: tutte le
 * 52.114 schede importate da OpenStreetMap, la ricerca, la directory condividono
 * lo stesso `<head>`. Prima di questo modulo ogni pagina scriveva solo
 * `document.title` e lasciava description, canonical e Open Graph con i valori
 * della home: 52.114 pagine con lo stesso titolo e lo stesso testo, cioe' 52.114
 * pagine che per un motore di ricerca sono la stessa pagina.
 *
 * E il problema inverso è altrettanto reale: se una pagina scrive i propri meta
 * e non li ripristina, il visitatore che passa dalla scheda di un hotel alla
 * ricerca porta con se' la description e il canonical dell'hotel. Sono segnali
 * che descrivono a Google una pagina che non sta piu' guardando.
 *
 * Per questo `useSeo` si fa carico del ciclo di vita: applica all'entrare e
 * **ripristina all'uscita**, riportando il `head` ai valori di `index.html`. Il
 * ripristino parte dagli originali presi una volta sola per documento, non da
 * quello che si trova al momento dello smontaggio: altrimenti, dopo due pagine
 * di fila, si ripristinerebbero i meta della pagina precedente invece di quelli
 * della home.
 *
 * Quello che NON fa, e che va detto perche' e' una scelta: non inventa prezzo,
 * disponibilita' ne' recensioni. Una scheda importata non ha camere ne' prezzi, e
 * un `aggregateRating` inventato è una recensione falsa in un risultato di
 * ricerca. Chi vuole quei campi li mette quando sono veri.
 */
import { useEffect } from "react";

import { DEFAULT_URL_LOCALE, localeOf, localeOfSlug, marketingPath, marketingPathOf } from "./localePath";

/**
 * Dominio pubblico: i canonical devono essere assoluti, `index.html` non lo sa.
 *
 * `REACT_APP_SITE_URL` prima del valore scritto qui. Il giorno in cui il sito
 * si sposta su un altro dominio, un canonical che resta sul vecchio e' una
 * dichiarazione al motore che la pagina nuova e' un duplicato di quella
 * vecchia: e' l'errore che costa piu' di tutti un trasloco, e costa una
 * variabile d'ambiente invece di una modifica in venti file. Il valore di
 * ripiego resta il dominio attuale, cosi' un build senza la variabile si
 * comporta come prima.
 */
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://hostaly.it")
  .replace(/\/+$/, "");

export const absoluteUrl = (path = "/") =>
  SITE_URL + (path.startsWith("/") ? path : `/${path}`);

/**
 * Il percorso canonico di una pagina nella lingua `lang`.
 *
 * Per le pagine di marketing lo slug **e'** la lingua (`/it/prezzi`,
 * `/en/pricing`): un canonical che tenesse `/prezzi` dichiarerebbe italiano un
 * indirizzo che l'utente ha aperto in inglese, e i due indirizzi diventerebbero
 * due pagine che competono. Per tutto il resto il percorso non cambia: le
 * rotte tecniche e le entita' (vetrina, citta', scheda) non hanno una sezione
 * linguistica propria, e inventarne una qui darebbe un canonical che il server
 * non serve.
 *
 * `lang` assente significa "la lingua che l'indirizzo dichiara": e' quella la
 * fonte di verita', non lo stato del provider — dopo un cambio lingua c'e' una
 * navigazione vera proprio perche' l'indirizzo deve cambiare.
 */
export function canonicalPath(path, lang) {
  const hit = marketingPathOf(path);
  if (!hit) return path;
  return marketingPath(hit.key, lang || DEFAULT_URL_LOCALE);
}

/** La lingua dell'indirizzo su cui stiamo scrivendo i meta, o `null`. */
function currentUrlLocale() {
  if (typeof window === "undefined") return null;
  const pathname = window.location?.pathname || "/";
  return localeOf(pathname) || localeOfSlug(pathname) || null;
}

/**
 * Sostituisce le annotazioni `hreflang` della pagina. Si tolgono **tutte**
 * quelle presenti, non solo le proprie: lo shell servito dal backend porta gia'
 * le varianti della pagina di partenza, e sommarle a quelle nuove direbbe al
 * motore che la pagina ha due serie di alternative che si contraddicono.
 */
export function setAlternates(doc, entries) {
  doc.head.querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
  (entries || []).forEach(({ href, hreflang }) => {
    const el = doc.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    el.setAttribute("href", href);
    doc.head.appendChild(el);
  });
}

/** Le due varianti reali di una pagina di marketing, piu' l'`x-default` italiano. */
export function marketingAlternates(path) {
  const it = absoluteUrl(canonicalPath(path, "it"));
  const en = absoluteUrl(canonicalPath(path, "en"));
  return [
    { hreflang: "it", href: it },
    { hreflang: "en", href: en },
    { hreflang: "x-default", href: it },
  ];
}

/**
 * I valori di partenza, presi da `index.html`. Non sono scritti a mano qui per un
 * secondo di verità: sono i valori che il documento aveva **prima** che una
 * pagina li cambiasse, e vengono letti una volta sola per documento.
 */
const PRISTINE = new WeakMap();

function snapshot(doc) {
  const metas = {};
  doc.head.querySelectorAll("meta").forEach((el) => {
    const attr = el.getAttribute("name") ? "name" : el.getAttribute("property") ? "property" : null;
    const key = attr && el.getAttribute(attr);
    if (!key) return;
    if (metas[key] === undefined) metas[key] = el.getAttribute("content") ?? null;
  });
  const canonical = doc.head.querySelector('link[rel="canonical"]');
  return {
    title: doc.title,
    metas,
    canonical: canonical ? canonical.getAttribute("href") : null,
  };
}

function pristine(doc) {
  if (!PRISTINE.has(doc)) PRISTINE.set(doc, snapshot(doc));
  return PRISTINE.get(doc);
}

/** Crea il meta se non esiste, poi gli mette il contenuto. */
function setMeta(doc, attr, key, content) {
  let el = doc.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = doc.createElement("meta");
    el.setAttribute(attr, key);
    doc.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(doc, href) {
  let el = doc.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = doc.createElement("link");
    el.setAttribute("rel", "canonical");
    doc.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Applica i metadati a `doc` e restituisce la funzione che li ripristina.
 * Esposta anche per i test: tutta la logica sta qui, l'hook e' solo `useEffect`.
 */
export function applySeo(doc, spec) {
  const before = pristine(doc);
  const lang = spec.lang || currentUrlLocale();
  const path = canonicalPath(spec.path || "/", lang);
  const url = absoluteUrl(path);

  if (spec.title) doc.title = spec.title;
  if (spec.description) setMeta(doc, "name", "description", spec.description);

  setCanonical(doc, url);
  if (spec.title) {
    setMeta(doc, "property", "og:title", spec.title);
    setMeta(doc, "name", "twitter:title", spec.title);
  }
  if (spec.description) {
    setMeta(doc, "property", "og:description", spec.description);
    setMeta(doc, "name", "twitter:description", spec.description);
  }
  setMeta(doc, "property", "og:url", url);
  setMeta(doc, "property", "og:type", spec.type || "website");
  if (spec.image) {
    setMeta(doc, "property", "og:image", absoluteUrl(spec.image));
    setMeta(doc, "name", "twitter:image", absoluteUrl(spec.image));
    setMeta(doc, "name", "twitter:card", "summary_large_image");
  } else {
    // Una pagina senza foto **non deve ereditare la foto di un'altra pagina**.
    // `index.html` porta il corporate hotel come anteprima della home: lasciarlo
    // qui significa che ogni scheda condivisa su WhatsApp mostra la foto di un
    // albergo che non e' quello descritto dal titolo. Meglio nessuna immagine
    // che un'immagine falsa: il link funziona lo stesso e dice la verita'.
    doc.head.querySelector('meta[property="og:image"]')?.remove();
    doc.head.querySelector('meta[name="twitter:image"]')?.remove();
    // `summary_large_image` senza immagine e' una card grande vuota: si
    // dichiara una scheda piccola, che e' quello che c'e' davvero da mostrare.
    if (before.metas["twitter:card"] != null) {
      setMeta(doc, "name", "twitter:card", "summary");
    } else {
      doc.head.querySelector('meta[name="twitter:card"]')?.remove();
    }
  }

  // `alternates: true` -> le due sezioni linguistiche della pagina;
  // `alternates: false` -> nessuna (una pagina `noindex` non si annuncia come
  // variante di niente); assente -> non si tocca, cosi' le pagine di entita'
  // continuano a usare le varianti che il server ha gia' iniettato per loro
  // (dipendono dal contenuto, non dallo slug).
  if (spec.alternates === false) setAlternates(doc, []);
  else if (spec.alternates) setAlternates(doc, marketingAlternates(path));

  // `noindex` è per le pagine che non devono entrare nell'indice. Se la pagina
  // non lo chiede, il tag non deve restare da una pagina precedente.
  const robots = doc.head.querySelector('meta[name="robots"]');
  if (spec.noindex) {
    if (!robots) {
      setMeta(doc, "name", "robots", "noindex");
    } else {
      robots.setAttribute("content", "noindex");
    }
  } else if (robots) {
    if (before.metas.robots == null) robots.remove();
    else robots.setAttribute("content", before.metas.robots);
  }

  let ld = doc.head.querySelector("script[data-seo-jsonld]");
  if (spec.jsonLd) {
    if (!ld) {
      ld = doc.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.setAttribute("data-seo-jsonld", "1");
      doc.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(spec.jsonLd);
  } else if (ld) {
    ld.remove();
  }

  return () => {
    doc.title = before.title;
    Object.entries(before.metas).forEach(([key, content]) => {
      const attr = doc.head.querySelector(`meta[name="${key}"]`)
        ? "name"
        : doc.head.querySelector(`meta[property="${key}"]`)
          ? "property"
          : null;
      if (!attr || content == null) return;
      setMeta(doc, attr, key, content);
    });
    const canonicalEl = doc.head.querySelector('link[rel="canonical"]');
    if (before.canonical == null) {
      if (canonicalEl) canonicalEl.remove();
    } else if (canonicalEl) {
      canonicalEl.setAttribute("href", before.canonical);
    }
    const script = doc.head.querySelector("script[data-seo-jsonld]");
    if (script) script.remove();
  };
}

/**
 * Metadati della pagina. Va chiamata in cima al componente, come qualsiasi hook:
 * il titolo dipende dai dati che arrivano dalla API, quindi si chiama anche
 * quando non c'è ancora niente da descrivere — semplicemente non fa niente finché
 * `title` è vuoto.
 */
export function useSeo(spec) {
  // Il JSON-LD si serializza una volta: un oggetto nuovo a ogni render farebbe
  // rilanciare l'effetto a ogni immagine e riscriverebbe il DOM di continuo.
  const jsonLd = spec.jsonLd ? JSON.stringify(spec.jsonLd) : "";
  const { title, description, path, image, type, noindex, alternates } = spec;
  useEffect(() => {
    if (!title) return undefined;
    return applySeo(document, {
      title,
      description,
      path,
      image,
      type,
      noindex,
      alternates,
      jsonLd: jsonLd ? JSON.parse(jsonLd) : null,
    });
  }, [title, description, path, image, type, noindex, alternates, jsonLd]);
}